<template>
  <button
    :type="type"
    class="f1-btn"
    :class="[`f1-btn--${variant}`, { 'f1-btn--block': block, 'f1-btn--lg': size === 'lg' }]"
    :disabled="disabled"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
/**
 * F1 风格主按钮 (大厅 lobby-figma 设计契约)
 *
 * 视觉规范来自登录页 .btn.btn-primary.btn-block.btn-lg.btn-skew:
 *   - 红色渐变 (var(--color-accent-gradient))
 *   - clip-path 斜切 polygon(8% 0, 100% 0, 92% 100%, 0 100%)
 *   - box-shadow 与 transform 反馈
 *
 * Token 走 theme.scss, 暗/亮主题自动适配.
 * 大厅及之后所有需要主按钮的页面统一用本组件,不再每页复制 CSS.
 *
 * 注: clip-path 会裁掉 box-shadow, shadow 声明保留只为与设计稿一致(暗色背景上视觉差异极小).
 */
withDefaults(
  defineProps<{
    variant?: 'primary' | 'ghost'
    size?: 'md' | 'lg'
    block?: boolean
    disabled?: boolean
    type?: 'button' | 'submit' | 'reset'
  }>(),
  {
    variant: 'primary',
    size: 'lg',
    block: false,
    disabled: false,
    type: 'button',
  }
)
</script>

<style scoped lang="scss">
.f1-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 22px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: transform 0.1s ease;
  clip-path: polygon(8% 0, 100% 0, 92% 100%, 0 100%);

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:not(:disabled):active {
    transform: scale(0.97);
  }
}

.f1-btn--primary {
  background: var(--color-accent-gradient);
  color: var(--color-accent-contrast, #fff);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--color-accent) 40%, transparent),
    0 6px 18px color-mix(in srgb, var(--color-accent) 25%, transparent);
}

.f1-btn--ghost {
  background: var(--color-surface, #15151e);
  border-color: var(--color-border-2, rgba(255, 255, 255, 0.1));
  color: var(--color-text, #fff);
}

.f1-btn--block {
  width: 100%;
}

.f1-btn--lg {
  height: 52px;
  padding: 0 26px;
  font-size: 15px;
}
</style>
